/**
 * ── What counts as a strong enough password ────────────────────────────────
 *
 * One admin account is a read of every customer, every lead and every render
 * in the console, so the bar here is deliberately above the server's minimum
 * rather than level with it. The API still checks its own rules; this file
 * exists so that the rejection arrives while the person is typing, as a list
 * of things to fix, instead of after a submit as one sentence.
 *
 * Every rule below is REQUIRED — the forms disable their submit button until
 * all of them pass. The strength score on top is advisory: it separates "long
 * enough to be allowed" from "long enough to be worth having", and it is the
 * only part that ever says something nice.
 */

export interface PasswordRule {
  id: string;
  /** Written as the finished state, so the checklist reads the same either way. */
  label: string;
  met: boolean;
}

export interface PasswordVerdict {
  rules: PasswordRule[];
  /** Every rule met. The forms refuse to submit without this. */
  ok: boolean;
  /** 0–4, advisory. Capped at 1 while any rule is still failing. */
  score: number;
  label: string;
  /** The single most useful next thing to do, or null when there isn't one. */
  advice: string | null;
}

/** Whose name and address this password must not contain. */
export interface Identity {
  email?: string;
  fullName?: string;
}

export const MIN_LENGTH = 12;

/**
 * Substrings that make a password guessable no matter what is wrapped around
 * them. Short by design: a real blocklist is millions of lines and belongs on
 * the server, and this one only has to catch the passwords people actually
 * reach for first — including the words this console is named after.
 */
const OBVIOUS = [
  "password",
  "qwerty",
  "qwertz",
  "azerty",
  "asdf",
  "zxcv",
  "letmein",
  "welcome",
  "admin",
  "administrator",
  "iloveyou",
  "monkey",
  "dragon",
  "football",
  "cricket",
  "sunshine",
  "princess",
  "master",
  "login",
  "secret",
  "changeme",
  "trustno",
  "jkmaxx",
  "maxx",
  "paint",
  "visualiser",
  "visualizer",
];

/** `p@55w0rd` is `password`. Folded before the blocklist, never before hashing. */
function fold(value: string): string {
  return value
    .toLowerCase()
    .replace(/[@4]/g, "a")
    .replace(/3/g, "e")
    .replace(/[1!|]/g, "l")
    .replace(/0/g, "o")
    .replace(/[5$]/g, "s")
    .replace(/7/g, "t");
}

/** `abcd`, `4321`, `aaa` — four in a row up or down, or three of the same. */
function hasRunOrRepeat(value: string): boolean {
  const lower = value.toLowerCase();

  if (/(.)\1\1/.test(lower)) return true;

  let run = 1;
  let direction = 0;
  for (let i = 1; i < lower.length; i += 1) {
    const step = lower.charCodeAt(i) - lower.charCodeAt(i - 1);
    const sequential =
      (step === 1 || step === -1) &&
      /[a-z0-9]/.test(lower[i]) &&
      /[a-z0-9]/.test(lower[i - 1]);

    if (sequential && (run === 1 || step === direction)) {
      run += 1;
      direction = step;
      if (run >= 4) return true;
    } else {
      run = sequential ? 2 : 1;
      direction = sequential ? step : 0;
    }
  }

  return false;
}

/**
 * The words a stranger holding the invitation already knows. An invite link
 * carries the address and the name, so a password built out of either is a
 * password the link itself gives away.
 */
function personalTokens({ email, fullName }: Identity): string[] {
  const tokens: string[] = [];

  const local = (email ?? "").split("@")[0] ?? "";
  for (const part of [local, ...local.split(/[^a-z0-9]+/i)]) {
    if (part.length >= 3) tokens.push(part.toLowerCase());
  }

  const domain = (email ?? "").split("@")[1] ?? "";
  for (const part of domain.split(".")) {
    // `com` and `co` would match half the dictionary; the name in front of
    // them is the part worth banning.
    if (part.length >= 4) tokens.push(part.toLowerCase());
  }

  for (const part of (fullName ?? "").split(/\s+/)) {
    if (part.length >= 3) tokens.push(part.toLowerCase());
  }

  return tokens;
}

function scoreOf(password: string): number {
  let points = 0;

  if (password.length >= MIN_LENGTH) points += 1;
  if (password.length >= 16) points += 1;
  if (password.length >= 20) points += 1;

  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((re) =>
    re.test(password),
  ).length;
  if (classes === 4) points += 1;

  // Length bought with `aaaaaaaaaaaa` is not length.
  if (new Set(password).size >= MIN_LENGTH) points += 1;

  return Math.min(4, points);
}

const LABELS = ["Too weak", "Weak", "Fair", "Strong", "Excellent"];

/** What to do about the one rule still failing. Never scolding, always a move. */
const ADVICE: Record<string, string> = {
  length: `A few more characters — ${MIN_LENGTH} is the minimum.`,
  case: "Add a capital letter, or make one lower case.",
  digit: "Add a number somewhere other than the end.",
  symbol: "Add a symbol somewhere other than the end.",
  personal: "Your name and email are on the invitation. Use neither.",
  obvious: "Swap the predictable part for something nobody would list.",
};

export function checkPassword(
  password: string,
  identity: Identity = {},
): PasswordVerdict {
  const folded = fold(password);
  const tokens = personalTokens(identity);

  const rules: PasswordRule[] = [
    {
      id: "length",
      label: `At least ${MIN_LENGTH} characters`,
      met: password.length >= MIN_LENGTH,
    },
    {
      id: "case",
      label: "Upper and lower case letters",
      met: /[a-z]/.test(password) && /[A-Z]/.test(password),
    },
    {
      id: "digit",
      label: "At least one number",
      met: /\d/.test(password),
    },
    {
      id: "symbol",
      label: "At least one symbol, like ! ? @ or -",
      met: /[^A-Za-z0-9]/.test(password),
    },
    {
      id: "personal",
      label: "Nothing from your name or email address",
      met:
        password.length > 0 &&
        !tokens.some((token) => folded.includes(fold(token))),
    },
    {
      id: "obvious",
      label: "No common words, runs like 1234, or repeats like aaa",
      met:
        password.length > 0 &&
        !OBVIOUS.some((word) => folded.includes(word)) &&
        !hasRunOrRepeat(password),
    },
  ];

  const failed = rules.filter((rule) => !rule.met);
  const ok = failed.length === 0 && password.length > 0;
  const score = ok ? scoreOf(password) : Math.min(1, scoreOf(password));

  let advice: string | null = null;
  if (password.length === 0) {
    advice = null;
  } else if (failed.length > 1) {
    advice = `${failed.length} things left to fix.`;
  } else if (failed.length === 1) {
    advice = ADVICE[failed[0].id] ?? null;
  } else if (score < 3) {
    advice = "Allowed — but length is what makes it hard to guess. Add a word.";
  }

  return { rules, ok, score, label: LABELS[score], advice };
}

/** The one question both forms ask before enabling their submit button. */
export function passwordsAcceptable(
  password: string,
  confirm: string,
  identity: Identity = {},
): boolean {
  return checkPassword(password, identity).ok && password === confirm;
}

/**
 * A password the rules above can't complain about, for people who would
 * rather not invent one. Words beat `Xk9#mQ2!` on both counts that matter
 * here: it clears the checklist, and it can be read off one screen and typed
 * into a phone without a mistake.
 */
const WORDS = [
  "amber", "anchor", "basil", "beacon", "birch", "camber", "canvas", "cedar",
  "cinder", "clover", "cobalt", "copper", "cypress", "dahlia", "ember", "fennel",
  "flint", "garnet", "ginger", "harbour", "indigo", "ivory", "juniper", "kestrel",
  "lantern", "linen", "magnet", "mango", "marble", "meadow", "mulberry", "nectar",
  "nimbus", "olive", "onyx", "orchid", "pebble", "pewter", "pigment", "plaster",
  "poppy", "quartz", "quince", "rattan", "saffron", "sandal", "sepia", "shale",
  "sienna", "silver", "sorrel", "spruce", "sumac", "teak", "thistle", "timber",
  "topaz", "tundra", "velvet", "walnut", "willow", "zinnia",
];

const SYMBOLS = "!?@#%&*-+=";

/**
 * 0 … max-1, uniformly. The obvious `random % max` quietly favours the low
 * end of the range, which is the one thing a password generator must not do.
 */
function randomInt(max: number): number {
  const limit = Math.floor(0x100000000 / max) * max;
  const buf = new Uint32Array(1);

  let value: number;
  do {
    crypto.getRandomValues(buf);
    value = buf[0];
  } while (value >= limit);

  return value % max;
}

function pick<T>(items: readonly T[]): T {
  return items[randomInt(items.length)];
}

const coinFlip = () => randomInt(2) === 0;

/** `amber` → `amBer`. Any letter, not the first one. */
function capitaliseOne(word: string): string {
  const at = randomInt(word.length);
  return word.slice(0, at) + word[at].toUpperCase() + word.slice(at + 1);
}

function shuffle<T>(items: readonly T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = randomInt(i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function suggestPassword(identity: Identity = {}): string {
  // Loops because the generator and the checker are independent on purpose:
  // if a word ever collides with somebody's surname, or two picks happen to
  // read as a run, the rules win and we draw again.
  for (let attempt = 0; attempt < 40; attempt += 1) {
    const words: string[] = [];
    while (words.length < 3) {
      const word = pick(WORDS);
      // Three different words, or the third one is decoration rather than
      // entropy — and `teak-Walnut-teak` reads like a typo besides.
      if (!words.includes(word)) words.push(word);
    }

    // A capital is required, so one word is drawn to carry one for certain and
    // the rest get one on a coin flip. The letter is any letter in the word:
    // a capital in the middle is worth more than a capital where every
    // password on earth already has one, and it costs nothing to type.
    const certain = randomInt(words.length);
    const cased = words.map((word, i) =>
      i === certain || coinFlip() ? capitaliseOne(word) : word,
    );

    // One to three digits, each drawn on its own — a group that can land
    // anywhere in the password rather than a year parked on the end.
    const digits = Array.from({ length: 1 + randomInt(3) }, () =>
      String(randomInt(10)),
    ).join("");

    // Where the digits sit among the words is itself a draw, and every join
    // is a separately chosen symbol.
    const candidate = shuffle([...cased, digits])
      .map((part, i) => (i === 0 ? part : pick([...SYMBOLS]) + part))
      .join("");

    if (checkPassword(candidate, identity).ok) return candidate;
  }

  // Unreachable in practice — 40 draws all failing the rules would need the
  // whole word list to collide with one person's name. A caller still gets
  // something usable rather than an empty field.
  return `${capitaliseOne(pick(WORDS))}${pick([...SYMBOLS])}${randomInt(10)}${pick([...SYMBOLS])}${pick(WORDS)}`;
}
